/**
 * Copyright IBM Corp. 2020, 2025
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { objectSpread2 as _objectSpread2, objectWithoutProperties as _objectWithoutProperties, extends as _extends, defineProperty as _defineProperty } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React__default from 'react';
import { EmptyStateV2 } from './EmptyStateV2.js';
import './ErrorEmptyState/ErrorEmptyState.js';
import './NoDataEmptyState/NoDataEmptyState.js';
import './NoTagsEmptyState/NoTagsEmptyState.js';
import './NotFoundEmptyState/NotFoundEmptyState.js';
import './NotificationsEmptyState/NotificationsEmptyState.js';
import './UnauthorizedEmptyState/UnauthorizedEmptyState.js';
import PropTypes from '../../_virtual/index.js';
import cx from 'classnames';
import { Link, Button } from '@carbon/react';
import { getDevtoolsProps } from '../../global/js/utils/devtools.js';
import '../../global/js/utils/props-helper.js';
import { pkg } from '../../settings.js';
import { EmptyStateContent } from './EmptyStateContent.js';

var _excluded = ["v2"],
  _excluded2 = ["action", "className", "illustration", "illustrationDescription", "illustrationPosition", "link", "size", "headingAs", "subtitle", "title"];

// The block part of our conventional BEM class names (blockClass__E--M).
var blockClass = "".concat(pkg.prefix, "--empty-state");
var componentName = 'EmptyState';
var sizes = /*#__PURE__*/function (sizes) {
  sizes["lg"] = "lg";
  sizes["sm"] = "sm";
  return sizes;
}(sizes || {}); // Default values for props
var defaults = {
  position: 'top',
  size: sizes.lg,
  headingAs: 'h3'
};
/**
 * The `EmptyState` component follows the Carbon guidelines for empty states with some added specifications around illustration usage. For additional usage guidelines and documentation please refer to the links above.
 */
var EmptyState = /*#__PURE__*/React__default.forwardRef(function (_ref, ref) {
  var _ref$v = _ref.v2,
    v2 = _ref$v === void 0 ? false : _ref$v,
    props = _objectWithoutProperties(_ref, _excluded);
  // todo: deprecate v1
  if (v2) {
    return /*#__PURE__*/React__default.createElement(EmptyStateV2, props);
  }
  var action = props.action,
    className = props.className,
    illustration = props.illustration,
    illustrationDescription = props.illustrationDescription,
    _props$illustrationPo = props.illustrationPosition,
    illustrationPosition = _props$illustrationPo === void 0 ? defaults.position : _props$illustrationPo,
    link = props.link,
    _props$size = props.size,
    size = _props$size === void 0 ? defaults.size : _props$size,
    _props$headingAs = props.headingAs,
    headingAs = _props$headingAs === void 0 ? defaults.headingAs : _props$headingAs,
    subtitle = props.subtitle,
    title = props.title,
    rest = _objectWithoutProperties(props, _excluded2);
  return /*#__PURE__*/React__default.createElement("div", _extends({}, rest, {
    className: cx(blockClass, "".concat(blockClass, "-type--default"), className, _defineProperty({}, "".concat(blockClass, "-position--").concat(illustrationPosition), !!illustration)),
    ref: ref
  }, getDevtoolsProps(componentName)), illustration && /*#__PURE__*/React__default.createElement("img", {
    src: illustration,
    alt: illustrationDescription,
    className: cx(["".concat(blockClass, "__illustration"), "".concat(blockClass, "__illustration--").concat(size)]),
    "aria-hidden": "true"
  }), /*#__PURE__*/React__default.createElement(EmptyStateContent, {
    action: action,
    link: link,
    size: size,
    headingAs: headingAs,
    subtitle: subtitle,
    title: title !== null && title !== void 0 ? title : ''
  }));
});

// Return a placeholder if not released and not enabled by feature flag
EmptyState = pkg.checkComponentEnabled(EmptyState, componentName);
EmptyState.propTypes = {
  /**
   * Empty state action button
   */
  action: PropTypes.shape(_objectSpread2(_objectSpread2({}, Button.propTypes), {}, {
    kind: PropTypes.oneOf(['primary', 'secondary', 'tertiary']),
    renderIcon: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    /**@ts-ignore*/
    onClick: Button.propTypes.onClick,
    text: PropTypes.string
  })),
  /**
   * Provide an optional class to be applied to the containing node.
   */
  className: PropTypes.string,
  /**
   * Empty state headingAs allows you to customize the type of heading element
   */
  headingAs: PropTypes.elementType,
  /**
   * Empty state illustration, specify the `src` for a provided illustration to be displayed. In the case of requiring a light and dark illustration of your own, simply pass the corresponding illustration based on the current theme of your application.
   * For example: `illustration={appTheme === 'dark' ? darkIllustration : lightIllustration}`
   */
  illustration: PropTypes.string,
  /**
   * The alt text for custom provided illustrations
   */
  /**@ts-ignore*/
  illustrationDescription: PropTypes.string.isRequired.if(function (_ref2) {
    var illustration = _ref2.illustration;
    return illustration;
  }),
  /**
   * Designates the position of the illustration relative to the content
   */
  illustrationPosition: PropTypes.oneOf(['top', 'right', 'bottom', 'left']),
  /**
   * Empty state link object
   */
  /**@ts-ignore*/
  link: PropTypes.shape(_objectSpread2(_objectSpread2({}, Link.propTypes), {}, {
    text: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    href: PropTypes.string
  })),
  /**
   * Empty state size
   */
  size: PropTypes.oneOf(['lg', 'sm']),
  /**
   * Empty state subtext
   */
  subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /**
   * Empty state heading
   */
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  /**
   * Designates which version of the EmptyState component is being used.
   * Refer to V2 documentation separately.
   */
  v2: PropTypes.bool
};
EmptyState.displayName = componentName;

export { EmptyState, defaults };
